import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container, Card, CardContent, Typography, TextField, Button, Grid, Box,
  FormControl, InputLabel, MenuItem, Select, Alert, Tooltip, CircularProgress,
  IconButton, InputAdornment, Paper
} from "@mui/material";
import {
  AutoAwesome, CalendarToday, Close,
  TipsAndUpdates, SchoolOutlined, CheckCircle, Info,
  Edit as EditIcon
} from '@mui/icons-material';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

function TrainingManagement() {
  const navigate = useNavigate();
  const location = useLocation();
  const [trainings, setTrainings] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [validationError, setValidationError] = useState("");
  const [isValidating, setIsValidating] = useState(false);
  const [validationDetails, setValidationDetails] = useState(null);
  const [autoFilling, setAutoFilling] = useState(false);
  const [fromGapAnalysis, setFromGapAnalysis] = useState(false);
  
  const [newTraining, setNewTraining] = useState({
    title: "", 
    trainer: "", 
    employee: "", 
    employee_id: "",
    email: "", 
    startDate: "", 
    endDate: "", 
    skillCategory: "", 
    skillContent: "",
    description: ""
  });

  const [manualCategory, setManualCategory] = useState("");
  const [manualContent, setManualContent] = useState("");
  const [showManualCategory, setShowManualCategory] = useState(false);
  const [showManualContent, setShowManualContent] = useState(false);

  const skillOptions = [
    "Process Management", "People Management", "Interpersonal Skills",
    "Communication Skills", "Managing Skills", "Technical Skills",
    "Cultural Skills", "Business Skills", "Accounting Skills", "Industrial Certificates"
  ];

  const skillContents = {
    "Process Management": ["Workflow Optimization", "Lean Six Sigma", "Agile Methodology"],
    "People Management": ["Team Leadership", "Conflict Resolution", "Performance Evaluation"],
    "Interpersonal Skills": ["Empathy Building", "Active Listening", "Networking Strategies"],
    "Communication Skills": ["Business Writing", "Presentation Skills", "Negotiation Techniques"],
    "Managing Skills": ["Time Management", "Risk Management", "Decision Making"],
    "Technical Skills": ["Python Programming", "Cloud Computing", "Data Analysis", "Machine Learning"],
    "Cultural Skills": ["Diversity Training", "Cross-Cultural Communication", "Inclusive Leadership"],
    "Business Skills": ["Market Analysis", "Strategic Planning", "Entrepreneurship"],
    "Accounting Skills": ["Financial Reporting", "Tax Planning", "Budgeting"],
    "Industrial Certificates": ["AWS Certified", "PMP Certification", "ISO Compliance"]
  };

  // Check for gap analysis data on component mount
  useEffect(() => {
    // Check location state first (from React Router navigation)
    if (location.state?.gapAnalysisData) {
      loadGapAnalysisData(location.state.gapAnalysisData);
    } 
    // Check localStorage as fallback
    else {
      const storedData = localStorage.getItem('trainingData');
      if (storedData) {
        try {
          const trainingData = JSON.parse(storedData);
          if (trainingData.fromGapAnalysis) {
            loadGapAnalysisData(trainingData);
            // Clear localStorage after loading
            localStorage.removeItem('trainingData');
          }
        } catch (error) {
          //console.error("Error parsing training data:", error);
        }
      }
    }
    
    fetchTrainings();
  }, [location]);

  const loadGapAnalysisData = async (data) => {
    setFromGapAnalysis(true);
    
    // Auto-fill employee ID and fetch employee details
    if (data.employee_id) {
      setNewTraining(prev => ({
        ...prev,
        employee_id: data.employee_id,
        employee: data.employeeName || "",
      }));
      
      // Fetch full employee details including email
      await autoFillEmployeeDetails(data.employee_id);
    }
    
    // Load recommendations count metadata without opening dialog
    if (data.recommendations && data.recommendations.length > 0) {
      setSuccess(
        `✨ Training data loaded from Gap Analysis! ` +
        `Employee: ${data.employeeName} | Project: ${data.projectName} | ` +
        `${data.missingSkills?.length || 0} skills need training`
      );
    }
  };

  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess("");
        setError("");
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  const fetchTrainings = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert("No authentication token found. Please log in.");
        return;
      }
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      const response = await axios.get(`${API_URL}/api/trainings`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      setTrainings(response.data);
    } catch (error) {
      //console.error("Error fetching trainings:", error);
      setError(error.response?.data?.message || "Failed to fetch trainings");
    }
  };

  // AI: Auto-fill employee details
  const autoFillEmployeeDetails = async (employeeId = null) => {
    const idToUse = employeeId || newTraining.employee_id;
    
    if (!idToUse || !idToUse.trim()) {
      setError("Please enter Employee ID first");
      return;
    }
    
    setAutoFilling(true);
    setValidationError("");
    
    try {
      const token = localStorage.getItem('token');
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      
      const url = `${API_URL}/api/trainings/ai/autofill/${idToUse.trim()}`;
      const response = await axios.get(url,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data.isValid && response.data.employee) {
        const employeeName = response.data.employee.name || '';
        const employeeEmail = response.data.employee.email || '';
        
        setNewTraining(prev => ({
          ...prev,
          employee: employeeName,
          email: employeeEmail
        }));
        
        if (!fromGapAnalysis) {
          setSuccess("✅ Employee details auto-filled successfully!");
        }
        setValidationError("");
      } else {
        setValidationError(response.data.message || "Employee not found");
        setNewTraining(prev => ({
          ...prev,
          employee: "",
          email: ""
        }));
      }
    } catch (error) {
      //console.error("Error in auto-fill:", error);
      setValidationError(error.response?.data?.message || "Employee ID not found in system");
      setNewTraining(prev => ({
        ...prev,
        employee: "",
        email: ""
      }));
    } finally {
      setAutoFilling(false);
    }
  };

  // Functions for fetching suggestions/recommendations removed to support pure manual entry

  const validateEmployeeDetails = async () => {
    if (!newTraining.employee_id || !newTraining.employee || !newTraining.email) {
      return true;
    }

    setIsValidating(true);
    setValidationDetails(null);
    
    try {
      const token = localStorage.getItem('token');
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      
      const response = await axios.post(`${API_URL}/api/trainings/validate-employee`, {
        employee_id: newTraining.employee_id.trim(),
        employee_name: newTraining.employee.trim(),
        email: newTraining.email.trim()
      },{
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": 'application/json'
        }
      });

      if (!response.data.isValid) {
        setValidationError(response.data.message || "Employee details do not match");
        if (response.data.details) {
          setValidationDetails(response.data.details);
        }
        return false;
      } else {
        setValidationError("");
        setValidationDetails(null);
        return true;
      }
    } catch (error) {
      //console.error("Error validating employee:", error);
      setValidationError(error.response?.data?.message || "Error validating employee details");
      return false;
    } finally {
      setIsValidating(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'skillCategory') {
      if (value === 'manual') {
        setShowManualCategory(true);
        setShowManualContent(false);
        setNewTraining(prev => ({
          ...prev,
          skillCategory: '',
          skillContent: ''
        }));
      } else {
        setShowManualCategory(false);
        setNewTraining(prev => ({
          ...prev,
          [name]: value,
          skillContent: ''
        }));
      }
    } else if (name === 'skillContent') {
      if (value === 'manual') {
        setShowManualContent(true);
        setNewTraining(prev => ({
          ...prev,
          skillContent: ''
        }));
      } else {
        setShowManualContent(false);
        setNewTraining(prev => ({
          ...prev,
          [name]: value
        }));
      }
    } else {
      setNewTraining(prev => ({
        ...prev,
        [name]: value
      }));
    }

    // Clear validation error when user starts typing
    if (name === 'employee_id' || name === 'employee' || name === 'email') {
      setValidationError("");
      setValidationDetails(null);
      setSuccess("");
    }

  };

  const handleManualCategoryChange = (e) => {
    const value = e.target.value;
    setManualCategory(value);
    setNewTraining(prev => ({
      ...prev,
      skillCategory: value
    }));
  };

  const handleManualContentChange = (e) => {
    const value = e.target.value;
    setManualContent(value);
    setNewTraining(prev => ({
      ...prev,
      skillContent: value
    }));
  };

  const handleBlur = async (e) => {
    const { name } = e.target;
    
    // Auto-fill when employee ID loses focus
    if (name === 'employee_id' && newTraining.employee_id && newTraining.employee_id.trim()) {
      await autoFillEmployeeDetails();
    }
  };

  const addTraining = async () => {
    // Check required fields
    const requiredFields = ['title', 'trainer', 'employee', 'employee_id', 'email', 'startDate', 'endDate', 'skillCategory', 'skillContent', 'description'];
    const missingFields = requiredFields.filter(field => !newTraining[field]);
    
    if (missingFields.length > 0) {
      setError(`Please fill all required fields: ${missingFields.join(', ')}`);
      return;
    }
  
    // Validate employee details before submitting
    const isValid = await validateEmployeeDetails();
    if (!isValid) return;
  
    const token = localStorage.getItem('token');
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }
  
    const payload = JSON.parse(atob(token.split('.')[1]));
    const company_id = payload.company_id;
  
    try {
      const response = await axios.post(
        `${API_URL}/api/trainings/add`,
        {
          ...newTraining,
          employee_id: newTraining.employee_id.trim(),
          employee: newTraining.employee.trim(),
          email: newTraining.email.trim()
        },{
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": 'application/json'
          }
        }
      );
  
      setTrainings([...trainings, response.data.training]);
      
      // Reset form
      setNewTraining({ 
        title: "", trainer: "", employee: "", employee_id: "", email: "", 
        startDate: "", endDate: "", skillCategory: "", skillContent: "", description: "" 
      });
      setManualCategory("");
      setManualContent("");
      setShowManualCategory(false);
      setShowManualContent(false);
      setFromGapAnalysis(false);
      
      setSuccess("✅ Training added successfully!");
      setTimeout(() => navigate('/trainings'), 2000);
  
    } catch (error) {
      //console.error("Error adding training:", error);
      setError(error.response?.data?.message || "Failed to add training");
    }
  };

  return (
    <Container maxWidth="1500px" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2, border: '2px solid', borderColor: 'grey.300', boxShadow: 4 }}>
        {success && (
          <Alert severity="success" sx={{ width: '100%', mb: 2 }}>
            {success}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
            {error}
          </Alert>
        )}
        {validationError && (
          <Alert severity="warning" sx={{ width: '100%', mb: 2 }}>
            {validationError}
          </Alert>
        )}
        
        {/* Gap Analysis Banner */}
        {fromGapAnalysis && (
          <Alert 
            severity="info" 
            icon={<TipsAndUpdates />}
            sx={{ mb: 3 }}
          >
            <Typography variant="body2" fontWeight="bold">
              Training data loaded from Gap Analysis
            </Typography>
            <Typography variant="caption">
              Employee details and recommendations have been pre-filled. Review and complete the form to schedule training.
            </Typography>
          </Alert>
        )}
        
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h4" fontWeight="bold">
              Knowledge Management
            </Typography>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField 
                required
                fullWidth 
                label="Employee ID" 
                name="employee_id" 
                value={newTraining.employee_id} 
                onChange={handleChange}
                onBlur={handleBlur}
                margin="normal"
                error={!!validationError}
                helperText={fromGapAnalysis ? "Auto-filled from Gap Analysis" : "Enter Employee ID - details will auto-fill on blur or click the sparkle icon"}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {autoFilling ? (
                        <CircularProgress size={20} />
                      ) : newTraining.employee_id && (
                        <Tooltip title="Click to auto-fill employee details">
                          <IconButton 
                            onClick={() => autoFillEmployeeDetails()}
                            size="small"
                            color="primary"
                          >
                            <AutoAwesome />
                          </IconButton>
                        </Tooltip>
                      )}
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField 
                required
                fullWidth 
                label="Employee Name" 
                name="employee" 
                value={newTraining.employee} 
                onChange={handleChange}
                margin="normal"
                error={!!validationError}
                helperText="Auto-filled from Employee ID"
                disabled={autoFilling}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField 
                required
                fullWidth 
                label="Employee Email" 
                name="email" 
                type="email" 
                value={newTraining.email} 
                onChange={handleChange}
                margin="normal"
                error={!!validationError}
                helperText="Auto-filled from Employee ID"
                disabled={autoFilling}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField 
                required
                fullWidth 
                label="Training Title" 
                name="title" 
                value={newTraining.title} 
                onChange={handleChange} 
                margin="normal"
                helperText={fromGapAnalysis ? "From AI recommendation - can be edited" : ""}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                required
                label="Trainer Name"
                margin="normal"
                fullWidth
                value={newTraining.trainer}
                onChange={(e) =>
                  setNewTraining((prev) => ({ ...prev, trainer: e.target.value }))
                }
              />
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Tooltip title="Select the main category of skills" arrow placement="top">
                <FormControl fullWidth margin="normal" required>
                  <InputLabel>Skill Category</InputLabel>
                  <Select
                    name="skillCategory"
                    value={showManualCategory ? 'manual' : newTraining.skillCategory}
                    label="Skill Category"
                    onChange={handleChange}
                  >
                    <MenuItem value="">Select Category</MenuItem>
                    {skillOptions.map((skill, index) => (
                      <MenuItem key={index} value={skill}>
                        {skill}
                      </MenuItem>
                    ))}
                    <MenuItem value="manual">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <EditIcon fontSize="small" />
                        Enter Manually
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Tooltip>
              
              {showManualCategory && (
                <TextField
                  fullWidth
                  label="Enter Custom Category"
                  value={manualCategory}
                  onChange={handleManualCategoryChange}
                  margin="normal"
                  required
                  helperText="Enter your own skill category"
                  sx={{ mt: 1 }}
                />
              )}
            </Grid>

            <Grid item xs={6}>
              <Tooltip title="Select specific content area" arrow placement="top">
                <FormControl fullWidth margin="normal" required>
                  <InputLabel>Skill Content</InputLabel>
                  <Select
                    name="skillContent"
                    label="Skill Content"
                    value={showManualContent ? 'manual' : newTraining.skillContent}
                    onChange={handleChange}
                    disabled={!newTraining.skillCategory}
                  >
                    <MenuItem value="">Select Content</MenuItem>
                    {newTraining.skillCategory && skillContents[newTraining.skillCategory]?.map((content, index) => (
                      <MenuItem key={index} value={content}>
                        {content}
                      </MenuItem>
                    ))}
                    <MenuItem value="manual">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <EditIcon fontSize="small" />
                        Enter Manually
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Tooltip>
              
              {showManualContent && (
                <TextField
                  fullWidth
                  label="Enter Custom Content"
                  value={manualContent}
                  onChange={handleManualContentChange}
                  margin="normal"
                  required
                  helperText="Enter your own skill content"
                  sx={{ mt: 1 }}
                />
              )}
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField 
                required
                fullWidth 
                type="date" 
                name="startDate" 
                label="Start Date" 
                value={newTraining.startDate} 
                onChange={handleChange} 
                InputLabelProps={{ shrink: true }} 
                margin="normal"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField 
                required
                fullWidth 
                type="date" 
                name="endDate" 
                label="End Date" 
                value={newTraining.endDate} 
                onChange={handleChange} 
                InputLabelProps={{ shrink: true }} 
                margin="normal"
              />
            </Grid>
          </Grid>

          <TextField
            required
            fullWidth
            multiline
            rows={4}
            label="Training Description"
            name="description"
            value={newTraining.description}
            onChange={handleChange}
            margin="normal"
          />
          
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={addTraining} 
              sx={{ mt: 2, width: "200px" }}
              disabled={!!validationError || isValidating}
            >
              Add Training
            </Button>
          </Box>
        </CardContent>
      </Paper>

    </Container>
  );
}

export default TrainingManagement;